if (!customElements.get('artwork-selector')) {
  customElements.define(
    'artwork-selector',
    class ArtworkSelector extends HTMLElement {
      connectedCallback() {
        this.form = document.getElementById(this.dataset.productFormId);
        this.product = JSON.parse(this.querySelector('[data-artwork-product]').textContent);
        this.motifIndex = this.findOptionIndex(this.dataset.motifOption, ['motif']);
        this.dimensionIndex = this.findOptionIndex(this.dataset.dimensionOption, ['dimensions', 'dimension', 'taille']);
        this.lotKeywords = this.keywords(this.dataset.lotKeywords);
        this.nameKeywords = this.keywords(this.dataset.nameKeywords);

        if (this.motifIndex < 0) return;
        this.addEventListener('change', this.onInternalChange);
        this.addEventListener('input', this.onInternalInput);
        document.addEventListener('change', this.onProductChange);
        this.refresh();
      }

      disconnectedCallback() {
        document.removeEventListener('change', this.onProductChange);
      }

      keywords(value) {
        return (value || '').split(',').map((item) => this.normalize(item)).filter(Boolean);
      }

      normalize(value) {
        return String(value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();
      }

      findOptionIndex(preferredName, fallbacks) {
        const names = [preferredName, ...fallbacks].map((name) => this.normalize(name));
        return this.product.options.findIndex((option) => names.includes(this.normalize(option)));
      }

      includesKeyword(value, keywords) {
        const normalized = this.normalize(value);
        return keywords.some((keyword) => normalized.includes(keyword));
      }

      selectedVariant() {
        const id = this.form?.querySelector('input[name="id"]')?.value;
        return this.product.variants.find((variant) => String(variant.id) === String(id));
      }

      onProductChange = (event) => {
        if (!event.target.closest(`#ProductInfo-${this.dataset.sectionId}`)) return;
        window.setTimeout(() => this.refresh(), 0);
      };

      refresh() {
        const variant = this.selectedVariant();
        if (!variant) return;

        const motif = variant.options[this.motifIndex];
        const isLot = this.includesKeyword(motif, this.lotKeywords);
        const isPersonalizedSingle = !isLot && this.includesKeyword(motif, this.nameKeywords);
        const lotComposer = this.querySelector('[data-lot-composer]');
        const single = this.querySelector('[data-single-personalization]');
        const singleName = this.querySelector('[data-single-name]');

        this.hidden = !isLot && !isPersonalizedSingle;
        lotComposer.hidden = !isLot;
        single.hidden = !isPersonalizedSingle;
        singleName.disabled = !isPersonalizedSingle;
        singleName.required = isPersonalizedSingle;

        this.querySelectorAll('[data-artwork-property]').forEach((input) => {
          input.disabled = !isLot;
          if (!isLot) input.value = '';
        });

        if (isLot) {
          this.renderChoices(variant);
          this.updateAllProperties();
        }
      }

      renderChoices(selectedVariant) {
        const dimension = this.dimensionIndex >= 0 ? selectedVariant.options[this.dimensionIndex] : null;
        const motifs = [];

        this.product.variants.forEach((variant) => {
          if (dimension && variant.options[this.dimensionIndex] !== dimension) return;
          const motif = variant.options[this.motifIndex];
          if (this.includesKeyword(motif, this.lotKeywords)) return;
          if (!motifs.some((item) => item.name === motif)) {
            motifs.push({ name: motif, image: variant.image, available: variant.available });
          }
        });

        this.querySelectorAll('[data-artwork-slot]').forEach((slot) => {
          const slotNumber = slot.dataset.artworkSlot;
          const current = slot.querySelector('.artwork-selector__radio:checked')?.value;
          const grid = slot.querySelector('[data-artwork-grid]');
          grid.innerHTML = motifs.map((motif, index) => this.cardMarkup(motif, slotNumber, index, current)).join('');
        });
      }

      cardMarkup(motif, slotNumber, index, current) {
        const id = `Artwork-${this.dataset.sectionId}-${slotNumber}-${index}`;
        const needsName = this.includesKeyword(motif.name, this.nameKeywords);
        const checked = motif.name === current ? ' checked' : '';
        const disabled = motif.available ? '' : ' disabled';
        const image = motif.image
          ? `<img src="${this.escapeAttribute(motif.image)}" alt="" width="180" height="180" loading="lazy" class="artwork-selector__image">`
          : '<span class="artwork-selector__image artwork-selector__image--empty"></span>';

        return `
          <div class="artwork-selector__choice">
            <input class="artwork-selector__radio visually-hidden" type="radio" id="${id}"
              name="artwork-choice-${this.dataset.sectionId}-${slotNumber}" value="${this.escapeAttribute(motif.name)}"
              data-requires-name="${needsName}" form="${this.dataset.productFormId}" required${checked}${disabled}>
            <label class="artwork-selector__card" for="${id}">
              ${image}<span class="artwork-selector__name">${this.escapeHtml(motif.name)}</span>
            </label>
          </div>`;
      }

      onInternalChange = (event) => {
        if (!event.target.matches('.artwork-selector__radio')) return;
        const slot = event.target.closest('[data-artwork-slot]');
        const nameWrap = slot.querySelector('[data-firstname-wrap]');
        const nameInput = slot.querySelector('[data-artwork-firstname]');
        const requiresName = event.target.dataset.requiresName === 'true';
        nameWrap.hidden = !requiresName;
        nameInput.required = requiresName;
        if (!requiresName) nameInput.value = '';
        this.updateProperty(slot);
      };

      onInternalInput = (event) => {
        if (event.target.matches('[data-artwork-firstname]')) {
          this.updateProperty(event.target.closest('[data-artwork-slot]'));
        }
      };

      updateAllProperties() {
        this.querySelectorAll('[data-artwork-slot]').forEach((slot) => this.updateProperty(slot));
      }

      updateProperty(slot) {
        const choice = slot.querySelector('.artwork-selector__radio:checked');
        const property = slot.querySelector('[data-artwork-property]');
        const nameInput = slot.querySelector('[data-artwork-firstname]');
        if (!choice) property.value = '';
        else if (choice.dataset.requiresName === 'true') {
          const firstname = nameInput.value.trim();
          property.value = firstname ? `Prénom : ${firstname}` : '';
        }
        else property.value = choice.value;
        this.updateSummary();
      }

      updateSummary() {
        const list = this.querySelector('[data-artwork-summary-list]');
        const variant = this.selectedVariant();
        const format = this.dimensionIndex >= 0 ? variant?.options[this.dimensionIndex] : '';
        const formatRow = format
          ? `<li class="artwork-selector__summary-format"><span>Format</span><strong>${this.escapeHtml(format)}</strong></li>`
          : '';
        const artworkRows = [...this.querySelectorAll('[data-artwork-slot]')].map((slot) => {
          const value = slot.querySelector('[data-artwork-property]').value || 'À choisir';
          return `<li><span>Affiche ${slot.dataset.artworkSlot}</span><strong>${this.escapeHtml(value)}</strong></li>`;
        }).join('');
        list.innerHTML = formatRow + artworkRows;
      }

      escapeHtml(value) {
        const span = document.createElement('span');
        span.textContent = value;
        return span.innerHTML;
      }

      escapeAttribute(value) {
        return this.escapeHtml(value).replace(/`/g, '&#96;');
      }
    }
  );
}
